import React, { useEffect, useState } from 'react';
import { translate } from '../../../services/translate';
import { Modal, View, ScrollView } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { WindowInformation } from '../../../services/window-information';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { ClientError, NotFound } from '../../../services/central-api/base-service';
import { AlreadySignedReport, armoryService, CannotEditFinishedArmoryShift, CannotSignArmoryShift } from '../../../services/central-api/armory';
import ConfirmIdentityModal from '../../../components/ConfirmIdentityModal';
import moment from 'moment';
import RejectionReasonModal from '../../../components/RejectionReasonModal';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSave: () => void;
    armoryShift: ArmoryShift;
    previousArmoryShift: ArmoryShift | null;
}

function ArmoryShiftReportModal({
    isModalVisible,
    setModalVisible,
    armoryShift,
    onSave,
    previousArmoryShift
}: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isConfirmIdentityModalVisible, setIsConfirmIdentityModalVisible] = useState(false);
    const [isRejectionModalVisible, setIsRejectionModalVisible] = useState(false);
    const [isSignHandover, setSignHandover] = useState<boolean>(false);

    useEffect(() => {
        setSignHandover(previousArmoryShift ? previousArmoryShift.secondArmourerSignedOn == null && previousArmoryShift.rejectionReason == null : false);
    }, [previousArmoryShift]);

    async function rejectHandoverReport(params: { rejectionReason: string; }) {
        try {
            if (isSignHandover && previousArmoryShift) {
                await armoryService.signHandoverReport(previousArmoryShift.id, params);
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setModalVisible(false);
            onSave();
        } catch (err) {
            if (err instanceof AlreadySignedReport || err instanceof CannotSignArmoryShift) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            } else if (err instanceof NotFound) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            } else if (err instanceof ClientError) {
                if (err.message === 'InvalidValue') {
                    Toast.show({
                        type: 'sentinelxError',
                        text1: translate('invalidPassword'),
                    });
                }
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function signArmoryShift(params: { password: string; }) {
        try {
            if (isSignHandover && previousArmoryShift) {
                await armoryService.signHandoverReport(previousArmoryShift.id, params);
            } else {
                await armoryService.finishArmoryShift(armoryShift.id, params);
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setModalVisible(false);
            onSave();
            return 'success';
        } catch (err) {
            if (err instanceof NotFound) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            } else if (err instanceof AlreadySignedReport || err instanceof CannotSignArmoryShift) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            } else if (err instanceof CannotEditFinishedArmoryShift) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            } else if (err instanceof ClientError) {
                if (err.message === 'InvalidValue') {
                    Toast.show({
                        type: 'sentinelxError',
                        text1: translate('invalidPassword'),
                    });
                    return 'failure';
                }
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return 'failure';
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
            }}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ gap: 8 }}>
                        <View style={styles.rowContainer}>
                            <MyAppText style={styles.armoryText}>{translate('serviceHandoverReport')}</MyAppText>
                            <View style={styles.badge('#D6D6D6')}>
                                <MyAppText style={styles.badgeText('#515151')}>{translate('notSigned')}</MyAppText>
                            </View>
                        </View>
                        <View style={styles.separator(theme.colors.primary['200'])}></View>
                    </View>
                    <ScrollView contentContainerStyle={{ gap: 10 }}>
                        <View style={{ gap: 8 }}>
                            {previousArmoryShift && isSignHandover &&
                                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <View style={{ flexDirection: 'row', gap: 3 }}>
                                        <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>
                                            {`${translate('previousArmourer')}:`}
                                        </MyAppText>
                                        <MyAppText style={styles.text}>
                                            {`${previousArmoryShift.user.warName || previousArmoryShift.user.name} -`}
                                        </MyAppText>
                                        <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>RF:</MyAppText>
                                        <MyAppText style={styles.text}>
                                            {previousArmoryShift.user.registry}
                                        </MyAppText>
                                    </View>
                                    <View style={styles.badge('#FDF6E5')}>
                                        <MyAppText style={styles.badgeText('#835E00')}>{moment(previousArmoryShift.definitiveStart).format('DD/MM/YYYY')}</MyAppText>
                                    </View>
                                </View>
                            }
                            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <View style={{ flexDirection: 'row', gap: 3 }}>
                                    <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>
                                        {`${translate('armourer')}:`}
                                    </MyAppText>
                                    <MyAppText style={styles.text}>
                                        {`${armoryShift.user.warName || armoryShift.user.name} -`}
                                    </MyAppText>
                                    <MyAppText style={[styles.text, { fontWeight: 'bold' }]}>RF:</MyAppText>
                                    <MyAppText style={styles.text}>
                                        {armoryShift.user.registry}
                                    </MyAppText>
                                </View>
                                <View style={styles.badge('#FDF6E5')}>
                                    <MyAppText style={styles.badgeText('#835E00')}>{moment(armoryShift.definitiveStart).format('DD/MM/YYYY')}</MyAppText>
                                </View>
                            </View>
                        </View>
                        <View style={{ gap: 8 }}>
                            <MyAppText style={styles.textTitle}>{`1. ${translate('dependencies')}`}</MyAppText>
                            <View style={{ paddingLeft: 22, paddingRight: 10 }}>
                                <MyAppText style={styles.text}>{isSignHandover ? previousArmoryShift?.dependencies : armoryShift.dependencies}</MyAppText>
                            </View>
                        </View>
                        <View style={{ gap: 8 }}>
                            <MyAppText style={styles.textTitle}>{`2. ${translate('documents')}`}</MyAppText>
                            <View style={{ paddingLeft: 22, paddingRight: 10 }}>
                                <MyAppText style={styles.text}>{isSignHandover ? previousArmoryShift?.documents : armoryShift.documents}</MyAppText>
                            </View>
                        </View>
                    </ScrollView>
                    <FormActions
                        confirmText='sign'
                        cancelText={isSignHandover ? 'reject' : undefined}
                        onSubmit={() => setIsConfirmIdentityModalVisible(true)}
                        onClose={() => isSignHandover ? setIsRejectionModalVisible(true) : setModalVisible(false)}
                        disabled={false}
                    />
                </View>
            </View>
            <ConfirmIdentityModal
                isModalVisible={isConfirmIdentityModalVisible}
                setModalVisible={setIsConfirmIdentityModalVisible}
                onSubmit={async (password) => {
                    return await signArmoryShift({ password });
                }}
            />
            <RejectionReasonModal
                isModalVisible={isRejectionModalVisible}
                setModalVisible={setIsRejectionModalVisible}
                onSubmit={async (rejectionReason) => {
                    return await rejectHandoverReport({ rejectionReason });
                }}
            />
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: (isMobile: boolean) => {
        return {
            minWidth: 300,
            maxHeight: '90%',
            backgroundColor: theme.colors.white['500'],
            borderRadius: 16,
            padding: 16,
            rowGap: 16,
            flexDirection: 'column',
            gap: 8,
            width: isMobile ? '95%' : 470
        };
    },
    rowContainer: {
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center'
    },
    armoryText: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16
    },
    textTitle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14
    },
    text: {
        color: theme.colors.neutral['400'],
        fontSize: 14
    },
    separator: (color: string) => {
        return {
            borderBottomWidth: 1,
            borderColor: color,
            flex: 1,
        };
    },
    badge: (backgroundColor: string) => {
        return {
            backgroundColor,
            borderRadius: 4,
            paddingVertical: 4,
            paddingHorizontal: 8
        };
    },
    badgeText: (color: string) => {
        return {
            color,
            fontSize: 14
        };
    },
}));


export default ArmoryShiftReportModal;
